/**
 * Versioned API Client for Edge Functions
 *
 * Centralized API client that handles:
 * - API versioning (automatic version headers)
 * - Authentication (Bearer tokens)
 * - Error handling (typed errors)
 * - Request/response logging
 * - Deprecation warnings
 *
 * @module apiClient
 * @version 1.0.0
 * @date 2025-11-20
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

/**
 * Current API version
 * Update this when migrating to a new API version
 */
const API_VERSION = 1;

/**
 * Base URL for Edge Functions
 * Automatically detects from Supabase client
 */
const getBaseURL = (): string => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL is not configured');
  }
  return `${supabaseUrl}/functions/v1`;
};

/**
 * API Error class with typed error codes
 */
export class APIError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

/**
 * API Client options
 */
export interface APIClientOptions {
  /**
   * Override default API version
   */
  version?: number;

  /**
   * Custom headers to add to request
   */
  headers?: Record<string, string>;

  /**
   * Request timeout in milliseconds
   * @default 30000 (30 seconds)
   */
  timeout?: number;

  /**
   * Whether to include authentication token
   * @default true
   */
  authenticate?: boolean;

  /**
   * Enable request/response logging
   * @default false (only in development)
   */
  debug?: boolean;
}

/**
 * Call an Edge Function with automatic versioning and error handling
 *
 * @example
 * ```typescript
 * // Basic usage
 * const result = await callEdgeFunction('check-subscription');
 *
 * // With request body
 * const result = await callEdgeFunction('ai-subsidy-helper', {
 *   method: 'POST',
 *   body: { description: 'agriculteur bourgogne' }
 * });
 *
 * // With custom version
 * const result = await callEdgeFunction('ai-subsidy-helper', {
 *   version: 2,
 *   method: 'POST',
 *   body: { description: 'agriculteur bourgogne' }
 * });
 * ```
 */
export async function callEdgeFunction<T = any>(
  endpoint: string,
  options: APIClientOptions & {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    body?: any;
  } = {}
): Promise<T> {
  const {
    version = API_VERSION,
    method = 'GET',
    body,
    headers = {},
    timeout = 30000,
    authenticate = true,
    debug = import.meta.env.DEV,
  } = options;

  const startTime = Date.now();
  const baseURL = getBaseURL();

  // Build URL (no /api/ prefix yet - that's for future versioning)
  // Current: /functions/v1/endpoint
  // Future: /functions/v1/api/v1/endpoint (when we migrate)
  const url = `${baseURL}/${endpoint}`;

  // Prepare headers
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  // Validate anon key is available
  if (!anonKey) {
    logger.error('[API] CRITICAL: VITE_SUPABASE_PUBLISHABLE_KEY is not defined! JWT verification will fail.');
    throw new Error('Supabase configuration error: VITE_SUPABASE_PUBLISHABLE_KEY is missing. Please restart the dev server.');
  }

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-API-Version': version.toString(),
    'apikey': anonKey, // Required for JWT verification with Edge Functions
    ...headers,
  };

  // Debug: Log headers in development
  if (import.meta.env.DEV && authenticate) {
    logger.debug(`[API] Request headers for ${endpoint}`, {
      hasApikey: !!requestHeaders.apikey,
      apikeyPreview: requestHeaders.apikey?.substring(0, 20) + '...',
      headerKeys: Object.keys(requestHeaders)
    });
  }

  // Add authentication token if requested
  if (authenticate) {
    // FIX: Use refreshSession() to ensure fresh access token
    // getSession() returns cached token which may be expired, causing 401 errors
    let token: string | undefined;

    // First try to refresh the session to get a fresh token
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

    if (refreshError || !refreshData.session) {
      // Fallback: try getSession() in case refresh fails but cached session is still valid
      if (import.meta.env.DEV) {
        logger.debug(`[API] Session refresh failed for ${endpoint}, trying cached session:`, refreshError?.message);
      }
      const { data: sessionData } = await supabase.auth.getSession();
      token = sessionData.session?.access_token;
    } else {
      token = refreshData.session.access_token;
    }

    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
      if (import.meta.env.DEV) {
        logger.debug(`[API] Auth token added for ${endpoint}`, {
          tokenPreview: token.substring(0, 20) + '...',
          authenticate,
          refreshed: !refreshError
        });
      }
    } else if (import.meta.env.DEV) {
      logger.warn('No auth token available', { endpoint, version, authenticate });
    }
  } else if (import.meta.env.DEV) {
    logger.debug(`[API] No auth required for ${endpoint}`, { authenticate });
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    // Log request (development only)
    if (debug) {
      logger.debug(`[API] ${method} ${endpoint}`, {
        version,
        body: body ? JSON.stringify(body).slice(0, 200) : undefined,
      });
    }

    // Make request
    const response = await fetch(url, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Check for deprecation warnings
    const deprecated = response.headers.get('X-API-Deprecated');
    if (deprecated === 'true') {
      const sunsetDate = response.headers.get('X-API-Sunset-Date');
      const warning = response.headers.get('Warning');

      // Log deprecation to Sentry and console
      logger.warn('API deprecation warning', {
        endpoint,
        version,
        sunsetDate,
        warning,
      });
    }

    // Parse response
    const data = await response.json();

    // Log response time (development only)
    if (debug) {
      const duration = Date.now() - startTime;
      logger.debug(`[API] ${endpoint} completed in ${duration}ms`, {
        status: response.status,
        success: data.success !== false,
      });
    }

    // Handle error responses
    if (!response.ok) {
      throw new APIError(
        data.error || 'UNKNOWN_ERROR',
        data.message || `Request failed with status ${response.status}`,
        response.status,
        data.details
      );
    }

    return data as T;
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle timeout
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new APIError(
        'TIMEOUT',
        `Request to ${endpoint} timed out after ${timeout}ms`,
        408
      );
    }

    // Handle network errors
    if (error instanceof TypeError) {
      throw new APIError(
        'NETWORK_ERROR',
        `Network error calling ${endpoint}: ${error.message}`,
        0
      );
    }

    // Re-throw API errors
    if (error instanceof APIError) {
      throw error;
    }

    // Unknown error
    throw new APIError(
      'UNKNOWN_ERROR',
      `Unknown error calling ${endpoint}: ${(error as Error).message}`,
      500
    );
  }
}

/**
 * Type-safe API clients for specific endpoints
 */
export const api = {
  /**
   * AI Subsidy Helper - AI-powered subsidy search
   */
  aiSubsidyHelper: async (params: {
    description: string;
    mode?: 'conversation' | 'analysis' | 'profile_conversation';
    conversationHistory?: Array<{ type: string; content: string }>;
    collectedInfo?: Record<string, any>;
    conversationMemory?: {
      previousSearches: string[];
      userPreferences: Record<string, any>;
      expressedConstraints: string[];
    };
    profileId?: string;
    fingerprint?: string;
  }) => {
    return callEdgeFunction<{
      success?: boolean;
      response?: string;
      analysis?: string;
      readyToSearch?: boolean;
      extractedInfo?: any;
      detectedFilters?: any;
      trialUsage?: { usageCount: number; remainingUses: number };
      error?: string;
    }>('ai-subsidy-helper', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Check Subscription - Get user's subscription status
   */
  checkSubscription: async () => {
    return callEdgeFunction<{
      subscribed: boolean;
      product_id: string | null;
      subscription_end: string | null;
      subscription_status: string | null;
    }>('check-subscription', {
      method: 'GET',
    });
  },

  /**
   * French Company Search - Search French companies by name, SIREN, or SIRET
   */
  searchFrenchCompanies: async (params: {
    query: string;
    per_page?: number;
  }) => {
    return callEdgeFunction<{
      results?: Array<{
        id: string;
        siren: string;
        siret?: string;
        name: string;
        address: string;
        postalCode: string;
        city: string;
        region?: string;
        nafCode: string;
        nafLabel: string;
        companyCategory?: string;
        employeeCount: string;
        legalForm?: string;
        creationDate?: string;
        administrativeStatus?: string;
        isEmployer?: boolean;
        isSocialEconomy?: boolean;
        isMissionCompany?: boolean;
        dirigeants?: Array<{
          nom?: string;
          prenoms?: string;
          qualite?: string;
        }>;
        establishments: Array<{
          siret: string;
          address: string;
          postalCode?: string;
          city?: string;
          nafCode?: string;
          tradeName?: string;
          isHeadOffice?: boolean;
          creationDate?: string;
          employeeCount?: string;
        }>;
      }>;
    }>('french-company-search', {
      method: 'POST',
      body: params,
      authenticate: true, // ✅ SECURITY FIX: Send auth token (user is logged in)
    });
  },

  /**
   * Enhanced French Company Search - Get detailed company information by SIREN
   */
  searchEnhancedFrenchCompanies: async (params: {
    query: string;
    filters?: { siren?: string };
  }) => {
    return callEdgeFunction<{
      companies?: Array<any>;
    }>('enhanced-french-company-search', {
      method: 'POST',
      body: params,
      authenticate: true, // ✅ SECURITY FIX: Send auth token (user is logged in)
    });
  },

  /**
   * Validate Company Registry - Validate French company by SIREN/SIRET
   */
  validateCompanyRegistry: async (identifier: string) => {
    return callEdgeFunction<{
      valid: boolean;
      companyData?: {
        siren: string;
        siret: string;
        name: string;
        address: string;
        legalForm: string;
        nafCode: string;
        creationDate: string;
      };
    }>('validate-company-registry', {
      method: 'POST',
      body: { identifier },
    });
  },

  /**
   * Geocode Address - Convert French address to coordinates
   */
  geocodeAddress: async (address: string) => {
    return callEdgeFunction<{
      latitude: number;
      longitude: number;
      region: string;
      department: string;
      city: string;
      postalCode: string;
    }>('geocode-address', {
      method: 'POST',
      body: { address },
    });
  },

  /**
   * Document Upload Handler - Upload and process document with OCR
   */
  uploadDocument: async (
    file: File,
    profileId?: string,
    extractionMode: 'text' | 'form' | 'table' | 'hybrid' = 'hybrid'
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (profileId) formData.append('profileId', profileId);
    formData.append('extractionMode', extractionMode);

    // Note: This uses FormData, so we need a special call
    const baseURL = getBaseURL();
    const url = `${baseURL}/document-upload-handler`;

    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-API-Version': API_VERSION.toString(),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new APIError(
        error.error || 'UPLOAD_ERROR',
        error.message || 'Document upload failed',
        response.status,
        error.details
      );
    }

    return response.json() as Promise<{
      success: boolean;
      documentId: string;
      extractedData: any;
      processingTime: number;
    }>;
  },

  /**
   * Validate AI Trial - Check if anonymous user can make AI requests
   */
  validateAITrial: async (fingerprint: string) => {
    return callEdgeFunction<{
      allowed: boolean;
      remainingUses: number;
      usageCount: number;
      reason?: string;
      message?: string;
    }>('validate-ai-trial', {
      method: 'POST',
      body: { fingerprint },
      authenticate: false, // Anonymous users
    });
  },

  /**
   * Search Consultants - Find consultants matching subsidy and user profile
   */
  searchConsultants: async (params: {
    subsidyId: string;
    userProfileId?: string | null;
    sortBy?: 'match_score' | 'reputation' | 'clients_helped';
    minMatchScore?: number;
    limit?: number;
  }) => {
    return callEdgeFunction<{
      success: boolean;
      consultants: Array<{
        id: string;
        company_name: string;
        headquarters_region?: string;
        reputation_score: number;
        total_clients_helped: number;
        platform_verified: boolean;
        matchScore?: number;
      }>;
      error?: string;
    }>('search-consultants', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Connect Consultant - Connect user with a consultant
   */
  connectConsultant: async (params: {
    consultantId: string;
    subsidyId: string;
    connectionType: 'email_request' | 'phone_call' | 'meeting';
    initialMessage?: string;
  }) => {
    return callEdgeFunction<{
      success: boolean;
      connectionId?: string;
      error?: string;
    }>('connect-consultant', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Send Consultant Info - Send consultant information to user
   */
  sendConsultantInfo: async (params: {
    consultantId: string;
    email: string;
    subsidyId?: string;
  }) => {
    return callEdgeFunction<{
      success: boolean;
      error?: string;
    }>('send-consultant-info', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Create Checkout Session - Create Stripe checkout session for subscription
   */
  createCheckoutSession: async (params: {
    tier: string;
    customerData?: {
      companyName?: string;
      siret?: string;
      address?: string;
      postalCode?: string;
      city?: string;
      inseeData?: any;
    };
    isTrialSignup?: boolean; // If true, creates subscription with 30-day trial period
  }) => {
    return callEdgeFunction<{
      url: string;
      sessionId: string;
    }>('create-checkout-session', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Send Expert Emails - Send emails to expert consultants
   */
  sendExpertEmails: async (params: {
    emailType: 'registration' | 'verification' | 'rejection' | 'welcome';
    expertId: string;
    customMessage?: string;
  }) => {
    return callEdgeFunction<{
      success: boolean;
      error?: string;
    }>('send-expert-emails', {
      method: 'POST',
      body: params,
    });
  },

  // ========================================================================
  // DOCUMENT PROCESSING ENDPOINTS
  // ========================================================================

  /**
   * Extract Document Data - Extract structured data from uploaded documents
   *
   * Uses hybrid extraction (OCR + AI) to extract data from PDFs, images, etc.
   */
  extractDocumentData: async (params: {
    documentId: string;
    fileUrl: string;
    fileName: string;
    clientType: 'farm' | 'individual' | 'business' | 'ngo';
    documentType: string;
  }) => {
    return callEdgeFunction<{
      success: boolean;
      extractedData: Record<string, any>;
      confidence: number;
      textLength: number;
      tokensUsed: number;
      ocrMetadata: Record<string, any>;
      qualityScore: number;
      extractionMethod: string;
      costBreakdown: Record<string, any>;
      processingTime: Record<string, any>;
      processingLog: string[];
    }>('extract-document-data', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Process Subsidy Documents - Process documents for a subsidy (async)
   *
   * Discovers and processes documents from subsidy URLs.
   * Returns immediately with processing status - check status later.
   */
  processSubsidyDocuments: async (params: {
    subsidyId: string;
    priority?: 'high' | 'normal';
    trigger?: 'user_click' | 'automatic';
    userId?: string;
  }) => {
    return callEdgeFunction<{
      success: boolean;
      cached?: boolean;
      processing?: boolean;
      result: {
        subsidyId: string;
        status: 'processing' | 'completed' | 'failed';
        documentsFound: number;
        processedDocuments: number;
        enrichmentData?: any;
        applicationSchema?: any;
        processingId: string;
        estimatedCompletion?: string;
        canApplyDirectly: boolean;
      };
    }>('process-subsidy-documents', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * On-Demand Document Processor - Process documents on demand (click handler)
   *
   * Similar to processSubsidyDocuments but with Google Cloud Document AI integration.
   */
  processDocumentsOnDemand: async (params: {
    subsidyId: string;
    trigger: 'thumbnail_click' | 'manual_request';
    priority: 'high' | 'normal';
    userId?: string;
  }) => {
    return callEdgeFunction<{
      success: boolean;
      cached: boolean;
      result: {
        subsidyId: string;
        documentsFound: number;
        documentsProcessed: number;
        applicationFormsGenerated: number;
        canApplyDirectly: boolean;
        enrichmentData: any;
        applicationSchema: any;
        processingCost: number;
      };
    }>('on-demand-document-processor', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Process Profile Document - Process document uploaded to user profile
   */
  processProfileDocument: async (params: {
    documentId: string;
    profileId?: string; // Optional - can be fetched from document record
    documentType?: string; // Optional - can be fetched from document record
  }) => {
    return callEdgeFunction<{
      success: boolean;
      extractedData: any;
      confidence: number;
    }>('process-profile-document', {
      method: 'POST',
      body: params,
      timeout: 120000, // 2 minutes (AI processing can take longer)
    });
  },

  // ========================================================================
  // ENRICHMENT & INTELLIGENCE ENDPOINTS
  // ========================================================================

  /**
   * Get Enriched Subsidy - Get subsidy with enriched data from document processing
   */
  getEnrichedSubsidy: async (subsidyId: string) => {
    return callEdgeFunction<{
      subsidyId: string;
      basicInfo: any;
      enrichmentData: {
        enhancedFundingInfo?: any;
        applicationProcess?: any;
        requiredDocuments?: string[];
        contactInfo?: any;
        deadlines?: any;
        eligibilityCriteria?: any;
        detailedRequirements?: any;
      };
      applicationForm?: {
        schema: any;
        canApplyDirectly: boolean;
        estimatedCompletionTime?: number;
      };
      processingMetadata: {
        lastProcessed: string;
        documentsAnalyzed: number;
        confidence: number;
        processingCost: number;
        sourceDocuments?: any[];
      };
      qualityScore: number;
      hasEnrichment: boolean;
    }>(`get-enriched-subsidy?subsidyId=${subsidyId}`, {
      method: 'GET',
    });
  },

  /**
   * Analyze Company Website - Extract business intelligence from company website
   *
   * Uses AI to analyze website and extract business activities, innovations, etc.
   */
  analyzeCompanyWebsite: async (params: {
    url: string;
    profileId?: string;
    language?: 'en' | 'fr';
  }) => {
    return callEdgeFunction<{
      success: boolean;
      intelligence: {
        companyDescription: string;
        businessActivities: string[];
        innovations: {
          indicators: string[];
          technologies: string[];
          score: number;
        };
        sustainability: {
          initiatives: string[];
          certifications: string[];
          score: number;
        };
        export: {
          markets: string[];
          multilingualSite: boolean;
          score: number;
        };
        digital: {
          technologies: string[];
          ecommerce: boolean;
          score: number;
        };
        growth: {
          signals: string[];
          recentInvestment: boolean;
          score: number;
        };
        analysis: {
          confidence: number;
          analysisDate: string;
          modelUsed: string;
        };
      };
      cached: boolean;
      error?: string;
    }>('analyze-company-website', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Enhanced Queue Processor - Process enrichment queue items
   *
   * Internal endpoint for queue processing (admin use).
   */
  processEnrichmentQueue: async (params: {
    batchSize?: number;
    maxProcessingTime?: number;
  }) => {
    return callEdgeFunction<{
      success: boolean;
      processed: number;
      failed: number;
      skipped: number;
      results: any[];
    }>('enhanced-queue-processor', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Get Processing Status - Check status of async document processing
   */
  getProcessingStatus: async (processingId: string) => {
    return callEdgeFunction<{
      id: string;
      subsidyId: string;
      status: 'processing' | 'completed' | 'failed';
      progress: number;
      documentsProcessed: number;
      documentsTotal: number;
      startedAt: string;
      completedAt?: string;
      error?: string;
    }>(`get-processing-status?processingId=${processingId}`, {
      method: 'GET',
    });
  },

  // ============================================================================
  // ADMIN & PIPELINE ENDPOINTS (Phase 3)
  // ============================================================================

  /**
   * Async Document Processor - Process documents asynchronously in background
   */
  asyncDocumentProcessor: async (params: {
    subsidyId: string;
    documentUrls: string[];
    processingMode?: 'full' | 'incremental';
    priority?: 'high' | 'normal' | 'low';
  }) => {
    return callEdgeFunction<{
      processingId: string;
      status: 'queued';
      estimatedCompletion: string;
    }>('async-document-processor', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Pipeline Orchestrator - Orchestrate multi-step data processing pipelines
   */
  pipelineOrchestrator: async (params: {
    pipelineType: 'enrichment' | 'extraction' | 'sync' | 'validation';
    targetIds: string[];
    config?: {
      parallel?: boolean;
      batchSize?: number;
      retryOnFailure?: boolean;
    };
  }) => {
    return callEdgeFunction<{
      pipelineId: string;
      status: 'started';
      stepsTotal: number;
      stepsCompleted: number;
    }>('pipeline-orchestrator', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Queue Seeder - Seed processing queues with subsidies for enrichment
   */
  queueSeeder: async (params: {
    source?: 'all' | 'aides-territoires' | 'les-aides' | 'manual';
    limit?: number;
    filters?: {
      region?: string;
      category?: string;
      status?: string;
    };
  }) => {
    return callEdgeFunction<{
      queued: number;
      skipped: number;
      errors: number;
      queueId: string;
    }>('queue-seeder', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Sync Aides Territoires - Sync subsidies from Aides-Territoires API
   */
  syncAidesTerritoires: async (params: {
    mode?: 'full' | 'incremental' | 'updates-only';
    region?: string;
    limit?: number;
    dryRun?: boolean;
  }) => {
    return callEdgeFunction<{
      synced: number;
      updated: number;
      created: number;
      errors: number;
      syncId: string;
    }>('sync-aides-territoires', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * AI Content Processor - Process content with AI (legacy)
   */
  aiContentProcessor: async (params: {
    subsidyId: string;
    content: string;
    processingType: 'extraction' | 'enrichment' | 'classification';
  }) => {
    return callEdgeFunction<{
      processed: boolean;
      extractedData?: any;
      confidence: number;
    }>('ai-content-processor', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * AI Content Processor V2 - Enhanced AI content processing
   */
  aiContentProcessorV2: async (params: {
    subsidyId: string;
    content: string;
    processingType: 'extraction' | 'enrichment' | 'classification' | 'validation';
    options?: {
      model?: 'claude-3-5-sonnet' | 'claude-3-opus';
      temperature?: number;
      maxTokens?: number;
    };
  }) => {
    return callEdgeFunction<{
      processed: boolean;
      extractedData?: any;
      confidence: number;
      tokensUsed: number;
      processingTime: number;
    }>('ai-content-processor-v2', {
      method: 'POST',
      body: params,
    });
  },

  // ============================================================================
  // TESTING & DEBUG ENDPOINTS (Phase 4)
  // ============================================================================

  /**
   * Test Enrichment Direct - Test enrichment without queueing
   */
  testEnrichmentDirect: async (subsidyId: string) => {
    return callEdgeFunction<{ success: boolean; data: any }>('test-enrichment-direct', {
      method: 'POST',
      body: { subsidyId },
    });
  },

  /**
   * Automatic Subsidy Enrichment - Enrich subsidy data automatically
   */
  automaticSubsidyEnrichment: async (subsidyId: string) => {
    return callEdgeFunction<{ enriched: boolean }>('automatic-subsidy-enrichment', {
      method: 'POST',
      body: { subsidyId },
    });
  },

  /**
   * Phase 2 Detail Scraper - Scrape detailed subsidy information (Phase 2)
   */
  phase2DetailScraper: async (subsidyId: string) => {
    return callEdgeFunction<{ scraped: boolean; data: any }>('phase2-detail-scraper', {
      method: 'POST',
      body: { subsidyId },
    });
  },

  /**
   * Pipeline Runs - Manage pipeline execution runs
   */
  pipelineRuns: async (action: 'create' | 'get' | 'update' | 'list', params: any) => {
    return callEdgeFunction<any>('pipeline-runs', {
      method: 'POST',
      body: { action, ...params },
    });
  },

  /**
   * QA Validation Agent - Validate data quality with AI agent
   */
  qaValidationAgent: async (params: { data: any; validationType: string }) => {
    return callEdgeFunction<{ valid: boolean; issues: string[] }>('qa-validation-agent', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Pipeline Health Monitor - Monitor pipeline health and performance
   */
  pipelineHealthMonitor: async (action: 'status' | 'metrics' | 'logs') => {
    return callEdgeFunction<any>('pipeline-health-monitor', {
      method: 'GET',
      body: { action },
    });
  },

  /**
   * Launch Validator - Validate system readiness for launch
   */
  launchValidator: async () => {
    return callEdgeFunction<{ ready: boolean; checks: any[] }>('launch-validator', {
      method: 'GET',
    });
  },

  /**
   * Hybrid Extraction - Hybrid OCR + AI extraction
   */
  hybridExtraction: async (params: { documentUrl: string; mode: string }) => {
    return callEdgeFunction<{ extracted: any }>('hybrid-extraction', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Hybrid OCR Extraction - Combined OCR and AI extraction
   */
  hybridOcrExtraction: async (params: { documentUrl: string }) => {
    return callEdgeFunction<{ text: string; structured: any }>('hybrid-ocr-extraction', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Health Check - Check Edge Function health
   */
  healthCheck: async () => {
    return callEdgeFunction<{ healthy: boolean; version: string }>('health-check', {
      method: 'GET',
    });
  },

  // ============================================================================
  // GDPR COMPLIANCE ENDPOINTS
  // ============================================================================

  /**
   * Consent Manager - Manage user consent preferences
   */
  consentManager: async (action: 'get' | 'update' | 'revoke', params?: any) => {
    return callEdgeFunction<any>('consent-manager', {
      method: 'POST',
      body: { action, ...params },
    });
  },

  /**
   * Data Export - Export user data for GDPR compliance
   */
  dataExport: async (userId: string) => {
    return callEdgeFunction<{ exportUrl: string }>('data-export', {
      method: 'POST',
      body: { userId },
    });
  },

  /**
   * Account Deletion - Delete user account and data
   */
  accountDeletion: async (userId: string, reason?: string) => {
    return callEdgeFunction<{ deleted: boolean }>('account-deletion', {
      method: 'POST',
      body: { userId, reason },
    });
  },

  // ============================================================================
  // LEGACY SCRAPERS
  // ============================================================================

  /**
   * FranceAgriMer Scraper - Scrape FranceAgriMer subsidies
   */
  franceagrimerScraper: async (params?: { limit?: number }) => {
    return callEdgeFunction<{ scraped: number }>('franceagrimer-scraper', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * FranceAgriMer Harvester - Harvest FranceAgriMer data
   */
  franceagrimerHarvester: async (params?: { mode?: string }) => {
    return callEdgeFunction<{ harvested: number }>('franceagrimer-harvester', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Enhanced FranceAgriMer Extraction - Enhanced extraction for FranceAgriMer
   */
  enhancedFranceagrimerExtraction: async (subsidyId: string) => {
    return callEdgeFunction<{ extracted: any }>('enhanced-franceagrimer-extraction', {
      method: 'POST',
      body: { subsidyId },
    });
  },

  /**
   * Enhanced Universal Scraper - Universal web scraper
   */
  enhancedUniversalScraper: async (params: { url: string; config?: any }) => {
    return callEdgeFunction<{ scraped: any }>('enhanced-universal-scraper', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Les Aides Health Check - Check Les Aides API health
   */
  lesaidesHealthcheck: async () => {
    return callEdgeFunction<{ healthy: boolean }>('lesaides-healthcheck', {
      method: 'GET',
    });
  },

  // ============================================================================
  // ADMIN TOOLS
  // ============================================================================

  /**
   * Admin Tier Switcher - Switch user tier (admin only)
   */
  adminTierSwitcher: async (params: { userId: string; tier: string }) => {
    return callEdgeFunction<{ updated: boolean }>('admin-tier-switcher', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Improve Subsidy Titles - AI-powered title improvement
   */
  improveSubsidyTitles: async (params?: { limit?: number }) => {
    return callEdgeFunction<{ improved: number }>('improve-subsidy-titles', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Data Purge - Purge old or invalid data
   */
  dataPurge: async (params: { type: string; olderThan?: string }) => {
    return callEdgeFunction<{ purged: number }>('data-purge', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Smart Change Detector - Detect changes in subsidy data
   */
  smartChangeDetector: async (subsidyId: string) => {
    return callEdgeFunction<{ changes: any[] }>('smart-change-detector', {
      method: 'POST',
      body: { subsidyId },
    });
  },

  /**
   * Dual Pipeline Orchestrator - Orchestrate dual processing pipelines
   */
  dualPipelineOrchestrator: async (params: any) => {
    return callEdgeFunction<{ orchestrated: boolean }>('dual-pipeline-orchestrator', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Aides Territoires Scheduler - Schedule Aides-Territoires syncs
   */
  aidesTerritoiresScheduler: async (params: { schedule: string }) => {
    return callEdgeFunction<{ scheduled: boolean }>('aides-territoires-scheduler', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Sync Job Processor - Process sync jobs
   */
  syncJobProcessor: async (jobId: string) => {
    return callEdgeFunction<{ processed: boolean }>('sync-job-processor', {
      method: 'POST',
      body: { jobId },
    });
  },

  // ============================================================================
  // DOCUMENT PROCESSING
  // ============================================================================

  /**
   * Extract Subsidy Form Fields - Extract form fields from subsidy documents
   */
  extractSubsidyFormFields: async (subsidyId: string) => {
    return callEdgeFunction<{ fields: any[] }>('extract-subsidy-form-fields', {
      method: 'POST',
      body: { subsidyId },
    });
  },

  /**
   * Extract Document Schemas - Extract schemas from documents
   */
  extractDocumentSchemas: async (documentUrl: string) => {
    return callEdgeFunction<{ schema: any }>('extract-document-schemas', {
      method: 'POST',
      body: { documentUrl },
    });
  },

  /**
   * Extract Document Schema - Extract single document schema
   */
  extractDocumentSchema: async (documentUrl: string) => {
    return callEdgeFunction<{ schema: any }>('extract-document-schema', {
      method: 'POST',
      body: { documentUrl },
    });
  },

  /**
   * Deep Structural Extraction - Deep structure extraction from documents
   */
  deepStructuralExtraction: async (params: { documentUrl: string; depth?: number }) => {
    return callEdgeFunction<{ structure: any }>('deep-structural-extraction', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Enhanced Subsidy Parser - Enhanced parsing for subsidy data
   */
  enhancedSubsidyParser: async (subsidyId: string) => {
    return callEdgeFunction<{ parsed: any }>('enhanced-subsidy-parser', {
      method: 'POST',
      body: { subsidyId },
    });
  },

  /**
   * Streaming Job Processor - Process jobs with streaming
   */
  streamingJobProcessor: async (jobId: string) => {
    return callEdgeFunction<{ status: string }>('streaming-job-processor', {
      method: 'POST',
      body: { jobId },
    });
  },

  /**
   * Batch Enhanced Parser - Parse subsidies in batch
   */
  batchEnhancedParser: async (subsidyIds: string[]) => {
    return callEdgeFunction<{ parsed: number }>('batch-enhanced-parser', {
      method: 'POST',
      body: { subsidyIds },
    });
  },

  /**
   * Unified Contextual Processor - Process with unified context
   */
  unifiedContextualProcessor: async (params: any) => {
    return callEdgeFunction<{ processed: any }>('unified-contextual-processor', {
      method: 'POST',
      body: params,
    });
  },

  // ============================================================================
  // FORM GENERATION & PROCESSING
  // ============================================================================

  /**
   * AI Prefill Form - Prefill application form with AI
   */
  aiPrefillForm: async (params: { subsidyId: string; profileData: any }) => {
    return callEdgeFunction<{ prefilled: any }>('ai-prefill-form', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Generate Form Schema - Generate form schema from subsidy
   */
  generateFormSchema: async (subsidyId: string) => {
    return callEdgeFunction<{ schema: any }>('generate-form-schema', {
      method: 'POST',
      body: { subsidyId },
    });
  },

  /**
   * Prefill Form - Prefill form with user data
   */
  prefillForm: async (params: { formId: string; userData: any }) => {
    return callEdgeFunction<{ prefilled: any }>('prefill-form', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Generate Application Form - Generate application form
   */
  generateApplicationForm: async (subsidyId: string) => {
    return callEdgeFunction<{ form: any }>('generate-application-form', {
      method: 'POST',
      body: { subsidyId },
    });
  },

  /**
   * Process Application Submission - Process form submission
   */
  processApplicationSubmission: async (params: { formId: string; data: any }) => {
    return callEdgeFunction<{ submitted: boolean; applicationId: string }>(
      'process-application-submission',
      {
        method: 'POST',
        body: params,
      }
    );
  },

  // ============================================================================
  // QUALITY & ANALYSIS
  // ============================================================================

  /**
   * Contextual Quality Calculator - Calculate quality with context
   */
  contextualQualityCalculator: async (subsidyId: string) => {
    return callEdgeFunction<{ quality: number; metrics: any }>(
      'contextual-quality-calculator',
      {
        method: 'POST',
        body: { subsidyId },
      }
    );
  },

  /**
   * Classify Extracted Fields - Classify extracted data fields
   */
  classifyExtractedFields: async (params: { fields: any[] }) => {
    return callEdgeFunction<{ classified: any[] }>('classify-extracted-fields', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * URL Health Checker - Check URL health and availability
   */
  urlHealthChecker: async (params: { url: string; subsidyId?: string; force?: boolean }) => {
    return callEdgeFunction<{
      success: boolean;
      healthCheck: {
        url: string;
        status: 'active' | 'dead_link' | 'potentially_obsolete' | 'unknown' | 'slow';
        httpStatus?: number;
        responseTime?: number;
        error?: string;
        lastChecked: string;
      };
    }>(
      'url-health-checker',
      {
        method: 'POST',
        body: {
          url: params.url,
          subsidyId: params.subsidyId,
          forceRefresh: params.force
        },
      }
    );
  },

  // ============================================================================
  // MATCHING & INTELLIGENCE
  // ============================================================================

  /**
   * Intelligent Subsidy Matcher - AI-powered subsidy matching
   */
  intelligentSubsidyMatcher: async (params: { profileId: string }) => {
    return callEdgeFunction<{ matches: any[] }>('intelligent-subsidy-matcher', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Match Subsidies - Match subsidies to profile
   */
  matchSubsidies: async (params: { profileId: string; filters?: any }) => {
    return callEdgeFunction<{ matches: any[] }>('match-subsidies', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Training Pipeline - Run AI training pipeline
   */
  trainingPipeline: async (action: string, params?: any) => {
    return callEdgeFunction<any>('training-pipeline', {
      method: 'POST',
      body: { action, ...params },
    });
  },

  // ============================================================================
  // BUSINESS SERVICES
  // ============================================================================

  /**
   * Company Enricher - Enrich company data
   */
  companyEnricher: async (params: { companyId?: string; siret?: string }) => {
    return callEdgeFunction<{ enriched: any }>('company-enricher', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Validate VAT - Validate VAT number
   */
  validateVat: async (vatNumber: string) => {
    return callEdgeFunction<{ valid: boolean; details: any }>('validate-vat', {
      method: 'POST',
      body: { vatNumber },
    });
  },

  /**
   * Get Financial Data - Get company financial data
   */
  getFinancialData: async (siret: string) => {
    return callEdgeFunction<{ data: any }>('get-financial-data', {
      method: 'POST',
      body: { siret },
    });
  },

  /**
   * Classify NACE - Classify business activity (NACE code)
   */
  classifyNace: async (activityDescription: string) => {
    return callEdgeFunction<{ naceCode: string; confidence: number }>('classify-nace', {
      method: 'POST',
      body: { activityDescription },
    });
  },

  /**
   * Check Sanctions - Check if company has sanctions
   */
  checkSanctions: async (companyId: string) => {
    return callEdgeFunction<{ sanctions: any[] }>('check-sanctions', {
      method: 'POST',
      body: { companyId },
    });
  },

  /**
   * Enrich Subsidy Assignment - Enrich subsidy assignments with AI-extracted data
   * Server-side processing: fetches subsidy page, extracts info, updates assignment
   */
  enrichSubsidyAssignment: async (params: { assignmentId: string; subsidyUrl: string; subsidyTitle: string }) => {
    return callEdgeFunction<{ success: boolean; enrichedData?: any; error?: string }>('enrich-subsidy-assignment', {
      method: 'POST',
      body: params,
    });
  },

  // ============================================================================
  // SECURITY & AUTH
  // ============================================================================

  /**
   * Auth Middleware - Authentication middleware validation
   */
  authMiddleware: async (token: string) => {
    return callEdgeFunction<{ valid: boolean; user: any }>('auth-middleware', {
      method: 'POST',
      body: { token },
    });
  },

  /**
   * Security Logger - Log security events
   */
  securityLogger: async (event: { type: string; data: any }) => {
    return callEdgeFunction<{ logged: boolean }>('security-logger', {
      method: 'POST',
      body: event,
    });
  },

  // ============================================================================
  // STRIPE & PAYMENTS
  // ============================================================================

  /**
   * Create Checkout - Create Stripe checkout session
   */
  createCheckout: async (params: { priceId: string; successUrl: string; cancelUrl: string }) => {
    return callEdgeFunction<{ sessionId: string; url: string }>('create-checkout', {
      method: 'POST',
      body: params,
    });
  },

  /**
   * Customer Portal - Create Stripe customer portal session
   */
  customerPortal: async (customerId: string) => {
    return callEdgeFunction<{ url: string }>('customer-portal', {
      method: 'POST',
      body: { customerId },
    });
  },

  /**
   * Create Billing Portal Session - Redirect to Stripe billing portal
   * Edge function determines customer from authenticated user's JWT token
   */
  createBillingPortalSession: async () => {
    return callEdgeFunction<{ url: string }>('customer-portal', {
      method: 'POST',
      body: {},
    });
  },

  /**
   * Pause Subscription - Pause user's active subscription
   * User retains access until end of current billing period
   */
  pauseSubscription: async () => {
    return callEdgeFunction<{
      success: boolean;
      action: 'pause';
      subscription: {
        id: string;
        status: string;
        current_period_end: string;
        paused_at: string;
      };
      message: string;
    }>('pause-subscription', {
      method: 'POST',
      body: { action: 'pause' },
    });
  },

  /**
   * Resume Subscription - Resume paused subscription
   * Billing restarts immediately
   */
  resumeSubscription: async () => {
    return callEdgeFunction<{
      success: boolean;
      action: 'resume';
      subscription: {
        id: string;
        status: string;
        current_period_end: string;
      };
      message: string;
    }>('pause-subscription', {
      method: 'POST',
      body: { action: 'resume' },
    });
  },

  // ============================================================================
  // TEAM MANAGEMENT
  // ============================================================================

  /**
   * Send Team Invitation - Send team invitation email
   */
  sendTeamInvitation: async (params: {
    email: string;
    token: string;
    organizationName: string;
    role: string;
    invitedByEmail: string;
  }) => {
    return callEdgeFunction<{ success: boolean; emailId: string }>('send-team-invitation', {
      method: 'POST',
      body: params,
    });
  },
};

/**
 * Handle API errors in UI components
 *
 * @example
 * ```typescript
 * try {
 *   await api.checkSubscription();
 * } catch (error) {
 *   handleAPIError(error, {
 *     onUnauthorized: () => router.push('/auth'),
 *     onRateLimit: () => toast.error('Too many requests. Please try again later.'),
 *   });
 * }
 * ```
 */
export function handleAPIError(
  error: unknown,
  handlers?: {
    onUnauthorized?: () => void;
    onForbidden?: () => void;
    onNotFound?: () => void;
    onRateLimit?: () => void;
    onServerError?: () => void;
    onNetworkError?: () => void;
    onDefault?: (error: APIError) => void;
  }
): void {
  if (!(error instanceof APIError)) {
    logger.error('Unexpected API error', error as Error, {
      errorType: error?.constructor?.name,
      errorMessage: (error as Error)?.message,
    });
    handlers?.onDefault?.(
      new APIError('UNKNOWN_ERROR', 'An unexpected error occurred', 500)
    );
    return;
  }

  // Handle specific error codes
  switch (error.status) {
    case 401:
      handlers?.onUnauthorized?.();
      break;
    case 403:
      if (error.code === 'RATE_LIMIT_EXCEEDED') {
        handlers?.onRateLimit?.();
      } else {
        handlers?.onForbidden?.();
      }
      break;
    case 404:
      handlers?.onNotFound?.();
      break;
    case 429:
      handlers?.onRateLimit?.();
      break;
    case 500:
    case 502:
    case 503:
      handlers?.onServerError?.();
      break;
    case 0:
      handlers?.onNetworkError?.();
      break;
    default:
      handlers?.onDefault?.(error);
  }
}
